import "./styles.scss";

import styled from "@emotion/styled";
import { Avatar, Badge, MenuItem, Tooltip } from "@mui/material";
import AlertTriangle from "assets/img/svg/alertTriangle";
import { SmallBody } from "helpers/TypographyComponents";
import { formatLocaleDateString, getInitials } from "helpers/utils";
import type { CSSProperties } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import variables from "../../../assets/styles/_variables.module.scss";
import StyledPopover from "../StyledPopover";

type TProps = {
    author: string;
    beneficiaryId?: number;
    representative?: string;
    lastSentAt?: Date | null | undefined;
    /**
     * label for last sent at
     */
    lastSentAtLabel?: string;
    createdDate?: Date | null | undefined;
    avatarUrl?: string;
    rowIndex?: number;
    /**
     * Comment to display if line is deleted.
     */
    deletedText?: string;
    /**
     * Reference for the term.
     */
    termReference?: string | null;
    /**
     * Handle search query
     */
    handleQuery?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    /**
     * Page where component is called
     */
    page?: "management" | "transfer-terms" | "payment" | "advance";
    nameStyle?: CSSProperties;
    /**
     * Condition to render avatar
     */
    showAvatar?: boolean;
    maxWidth?: string;
    /**
     * If true, will show a warning icon indicating that the beneficiary profile is incomplete.
     */
    isProfileIncomplete?: boolean;
};

const styleAvatar = {
    backgroundColor: variables["beneficiary-avatar-background"],
    color: variables["beneficiary-avatar-letter"],
    fontSize: "10px!important",
    width: "24px",
    height: "24px",
    fontWeight: 500,
};

type TStyled = {
    beneficiaryId: number | undefined;
};

const StyledSpan = styled.span<TStyled>((props) => ({
    fontWeight: 500,
    textDecoration: props.beneficiaryId ? "underline" : "none",
    cursor: props.beneficiaryId ? "pointer" : "auto",
}));

function ManagementTableAuthorComponent({
    author,
    beneficiaryId,
    representative,
    lastSentAt,
    lastSentAtLabel,
    createdDate,
    avatarUrl,
    rowIndex,
    deletedText,
    termReference,
    handleQuery,
    page,
    nameStyle,
    showAvatar = true,
    maxWidth,
    isProfileIncomplete,
}: TProps) {
    const [t] = useTranslation();
    const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
    const showPopover = Boolean(anchorEl);
    const closePopover = () => setAnchorEl(null);
    const navigate = useNavigate();
    const isManagementPage = page === "management";
    /**
     * On these pages we are showing menu to navigate/filter beneficiary
     */
    const pagesToShowMenu = ["management", "transfer-terms", "payment", "advance"];

    const handleNavigateToBeneficiary = (e) => {
        e.stopPropagation(); // prevent open drawer
        if (showPopover) return;
        if (pagesToShowMenu.includes(page!)) {
            setAnchorEl(e.currentTarget);
            return;
        }
        navigateToBeneficiary();
    };

    const navigateToBeneficiary = () => {
        if (page === "transfer-terms") {
            navigate(`/transferees/${beneficiaryId}`);
        } else {
            navigate(`/beneficiars/${beneficiaryId}`);
        }
    };

    const handleSearchForBeneficiary = () => {
        closePopover();
        handleQuery!({ target: { value: author } } as React.ChangeEvent<HTMLInputElement>);
    };

    return (
        <div className="author-component">
            {showAvatar && (
                <Tooltip
                    title={
                        lastSentAt != null && lastSentAt != undefined
                            ? `${lastSentAtLabel ?? t("Management.MailSentAt")} ${formatLocaleDateString({
                                  date: lastSentAt,
                                  options: { numeric: true, timeZone: "UTC" },
                              })}`
                            : ""
                    }
                >
                    <Badge
                        badgeContent={lastSentAt != null && lastSentAt != undefined ? 1 : 0}
                        variant="dot"
                        overlap="circular"
                    >
                        <Avatar
                            sx={styleAvatar}
                            variant="circular"
                            src={avatarUrl}
                            className={`general-table-row-${rowIndex}-author-image`}
                        >
                            {getInitials(author ?? "")}
                        </Avatar>
                    </Badge>
                </Tooltip>
            )}
            <div className="author-component-bloc" style={{ maxWidth }}>
                {author.trim() ? (
                    <div className="flex items-center gap-2">
                        <Tooltip title={author}>
                            <StyledSpan
                                id={`general-table-row-${rowIndex}-author-name`}
                                beneficiaryId={beneficiaryId}
                                onClick={handleNavigateToBeneficiary}
                                className="author-component-name flex-1! min-w-0! w-full! text-ellipsis! overflow-hidden!"
                                style={{ ...nameStyle }}
                            >
                                {author}
                                <StyledPopover open={showPopover} onClose={closePopover} anchorEl={anchorEl}>
                                    <MenuItem onClick={navigateToBeneficiary}>
                                        <SmallBody>{t("Management.NavigateToBeneficiary")}</SmallBody>
                                    </MenuItem>
                                    <MenuItem onClick={handleSearchForBeneficiary}>
                                        <SmallBody>{t("Management.FilterBeneficiary")}</SmallBody>
                                    </MenuItem>
                                </StyledPopover>
                            </StyledSpan>
                        </Tooltip>
                        {isProfileIncomplete ? (
                            <Tooltip title={t("Payment.IncompleteBeneficiaryTooltip")}>
                                <div className="flex-none! flex items-center! gap-1">
                                    <AlertTriangle className="size-4!" color={variables["dark-red"]} />
                                    <span className="text-[10px]!" style={{ color: variables["dark-red"] }}>
                                        {t("Payment.IncompleteBeneficiaryMessage")}
                                    </span>
                                </div>
                            </Tooltip>
                        ) : null}
                    </div>
                ) : (
                    <span
                        style={{
                            color: "rgba(0, 0, 2, 0.5647058824)",
                            fontSize: "11px",
                            fontStyle: "italic",
                        }}
                    >
                        {deletedText ?? t("Titles.AuthorRemoved")}
                    </span>
                )}
                {representative ? (
                    <span style={{ fontSize: "12px", color: variables["foreground"] }}>{representative}</span>
                ) : (
                    ""
                )}
                {termReference ? (
                    <span style={{ fontSize: "12px", color: variables["foreground"] }}>#{termReference}</span>
                ) : (
                    ""
                )}
                {createdDate ? (
                    <span style={{ fontSize: "12px", color: variables["foreground"] }}>
                        {t("Management.CreatedAt")}{" "}
                        {formatLocaleDateString({ date: createdDate, options: { numeric: true, timeZone: "UTC" } })}
                    </span>
                ) : (
                    ""
                )}
            </div>
        </div>
    );
}

export default ManagementTableAuthorComponent;
